import { pageQueryParams } from '@/components/search/pageQueryParams'
import { SortableHeader } from '@/components/search/SortableHeader'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { addServerErrors } from '@/components/forms/common/FormBase'
import { useForm } from 'react-hook-form'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { useModal } from '@/components/modal/Modal'
import { formatDate } from '@/common/time'
import { isDesktopScreen, isLargeScreen, useResponsiveChange } from '@/common/device'
import { BackButton } from '@/components/layout/NavBar'
import { CommonUsernameInput, UsernameInput } from '@/components/forms/common/user/input'
import { Spinner } from '@/components/spinner'
import { UCIDInput } from '@/components/forms/common/cards/input'
import { CardFormData, ManagerRemoveCardPopup, SimpleCard } from './common'
import { FormErrors } from '@/common/fetch'
import { useAppContext } from '@/context/app'
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Roles } from '@/services/repository/users'
import { ManagerEditUsersPopup } from '@/pages/for/companymanager/myusers/users'
import { SuperAdminEditUsersPopup } from '@/pages/for/superadmin/users/Users'
import { extendCommunalCardsMenuPopup } from '@/env/communal/cards/menu'

export interface ManagerEditCardPopupProps {
  onClose: () => void
  ucid: string | undefined
}

export interface ManagerInsertCardPopupProps {
  services: Services
  session: CurrentSession
  onClose: () => void
}

export interface UserRow {
  id: string
  username: string
}

export const ManagerEditCardPopup = ({ onClose, ucid }: ManagerEditCardPopupProps) => {
  const form = useForm<CardFormData>()
  const { openModal } = useModal()
  const { services, session, translates } = useAppContext()
  const [data, setData] = useState<CardFormData>()
  const [userId, setUserId] = useState<CardFormData>()
  const [error, setError] = useState<string | null>(null)
  const [serverErrors, setServerErrors] = useState<FormErrors<CardFormData>>()
  const formValues = form.watch()
  const isValid = form.formState.isValid

  useEffect(() => {
    if (isValid) {
      setServerErrors(undefined)
      setError(null)
    }
  }, [formValues, isValid])

  // useEffect(() => {
  //     setUserId(undefined)
  // }, [formValues.ucid])

  useEffect(() => {
    if (!ucid) return
    setData(undefined)
    services.session.fetchWithToken(`/api/communal/cards/byid/${ucid}`).then(r => r.json()).then((data) => {
      setUserId(data.userId)
      setData(data)
      form.reset(data)
    })
  }, [ucid])

  const handle = (data: CardFormData): Promise<void> => {
    if (!translates || !session) {
      return Promise.reject()
    }
    if (!data.username) {
      console.error('username is required')
      return Promise.reject()
    }
    if (!form) return Promise.reject()
    return services.session.fetchWithToken(`/api/communal/cards/byid/${ucid}/update`, {
      method: 'PUT',
      body: JSON.stringify(form.getValues())
    }).then(async (r) => {
      if (r.status !== 200) {
        setError('Write data error')
        const respData = await r.json()
        addServerErrors(form, translates, respData.errors)
        setServerErrors(respData.errors)
        return
      }
      setError(null)
      onClose()
    })
  }

  if (!translates || !session) {
    return <Spinner />
  }

  return data
    ? <form className="main-form cards-form" onSubmit={form.handleSubmit(handle)}>
      <div className="form-field" >
        <label htmlFor="user">{translates.for("forms.card.fields.userId.title")}:</label>
        <UsernameInput
          fieldName="username"
          formName="card"
          form={form}
          services={services}
          translates={translates}
          readOnly={false}
          required={true}
        />
      </div>
      <div className="form-field">
        <label htmlFor="ucid">{translates.for("forms.card.fields.ucid.title")}:</label>
        <UCIDInput
          fieldName="ucid"
          formName="card"
          form={form}
          services={services}
          translates={translates}
          readOnly={false}
          required={true}
        />
      </div>
      {/* <div className="form-field">
        <label htmlFor="label">{session.translates.for("forms.card.fields.label.title")}:</label>
        <input
          type="text"
          id="label"
          {...form.register('label')}
          name="label"
        />
      </div> */}
      {error && !serverErrors && <div className="critic-error-box">{translates.for("errors.general_error")}</div>}
      {serverErrors && <div className="critic-error-box">
        {serverErrors &&
          Object.entries(serverErrors).map(([field, error]) => (
            <div key={field}>{translates.for(error.message)}</div>
          ))}
      </div>}
      {/* <button className="submit-button" type="submit" disabled={
        !data.username || !data.ucid
      }>
        {translates.for("forms.card.submit")}
      </button> */}

      <div className='form-buttons'>
        <button className="submit-button" type="submit" disabled={
          !data.username || !data.ucid
        }>
          {translates.for("forms.card.submit")}
        </button>

        {userId ? <button className="alter-button" onClick={() => {
          onClose()
          if (!userId) {
            return
          }
          switch (session?.user.role) {
            case Roles.CompanyManager:
              return openModal((props) => ManagerEditUsersPopup({ ...props, id: userId }))
            case Roles.HyperAdmin:
            case Roles.SuperAdmin:
              return openModal((props) => SuperAdminEditUsersPopup({ ...props, id: userId }))
          }
          ManagerEditCardPopup({ ...props, ucid: formValues.ucid })
        }}>
          {session.translates.for("forms.card.edit_user")}
        </button> : null}
      </div>


    </form>
    : <div className="spinner"></div>
}

export const ManagerInsertCardPopup = ({ services, session, onClose }: ManagerInsertCardPopupProps) => {
  const form = useForm<CardFormData>({
    mode: "onChange",
    defaultValues: {
      ucid: '',
      username: ''
    }
  })
  const { openModal } = useModal()
  const [serverErrors, setServerErrors] = useState<FormErrors<CardFormData>>()
  const [error, setError] = useState<string | null>(null)
  const formValues = form.watch()
  const isValid = form.formState.isValid

  useEffect(() => {
    if (isValid) {
      setServerErrors(undefined)
      setError(null)
    }
  }, [formValues, isValid])

  const handle = (data: CardFormData): Promise<void> => {
    if (!data.username) {
      console.error('user is required')
      return Promise.reject()
    }
    if (!data.ucid) {
      console.error('ucid is required')
      return Promise.reject()
    }
    if (!form) return Promise.reject()
    setError(null)
    return services.session.fetchWithToken(`/api/communal/cards/new`, {
      method: 'POST',
      body: JSON.stringify(data)
    }).then(async (r) => {
      if (r.status !== 200) {
        setError('Write data error')
        const respData = await r.json()
        addServerErrors(form, session.translates, respData.errors)
        setServerErrors(respData.errors)
        return
      }
      setError(null)
      form.reset()
      onClose()
    })
  }

  if (!session || !services) {
    return <Spinner />
  }

  return <form className="main-form cards-form" onSubmit={form.handleSubmit(handle)}>
    <div className="form-field" >
      <label htmlFor="user">{session.translates.for("forms.card.fields.username.title")}:</label>
      {/* <CommonUsernameInput fieldName='userId' formName='card' setValue={(username) => {
        setData({
          ...data,
          username
        })
      }} value={data.username} services={services} session={session} translates={session.translates} /> */}
      <UsernameInput
        fieldName="username"
        formName="card"
        form={form}
        services={services}
        translates={session.translates}
        readOnly={false}
        required={true}
      />
    </div>
    <div className="form-field">
      <label htmlFor="ucid">{session.translates.for("forms.card.fields.ucid.title")}:</label>
      <UCIDInput
        fieldName="ucid"
        formName="card"
        form={form}
        services={services}
        translates={session.translates}
        readOnly={false}
        required={true}
      />
    </div>
    {/* <div className="form-field">
      <label htmlFor="label">{session.translates.for("forms.card.fields.label.title")}:</label>
      <input
        type="text"
        id="label"
        {...form.register('label')}
        name="label"
      />
    </div> */}
    {error && !serverErrors && <div className="critic-error-box">{session.translates.for("errors.general_error")}</div>}
    {serverErrors && <div className="critic-error-box">
      {serverErrors &&
        Object.entries(serverErrors).map(([field, error]) => (
          <div key={field}>{session.translates.for(error.message)}</div>
        ))}
    </div>}

    <div className='form-buttons'>
      <button className="submit-button" type="submit" disabled={!form.formState.isValid}>
        {session.translates.for("forms.card.submit")}
      </button>

      {serverErrors?.ucid ? <button className="alter-button" onClick={() => {
        onClose()
        openModal((props) => ManagerEditCardPopup({ ...props, ucid: formValues.ucid }))
      }}>
        {session.translates.for("forms.card.edit_row_on_exists")}
      </button> : null}
    </div>

  </form>
}

export const CardsMenuPopup = ({ onClose, row }: {
  onClose: () => void
  row: SimpleCard
}) => {
  const navigate = useNavigate()
  const { services, session, translates } = useAppContext()
  const { openModal } = useModal()

  if(!session) {
    return <Spinner />
  }

  let menu: Menu = {
    title: `${row.username}`,
    rows: [
      {
        icon: 'svgfont-edit',
        title: 'elements.table.cards.menu.edit.edit_row',
        action: () => openModal((props) => ManagerEditCardPopup({ ...props, ucid: row.ucid }))
      },
      {
        icon: 'svgfont-users',
        title: 'elements.table.cards.menu.edit.edit_user',
        action: () => {
          if (!row.userId) {
            return
          }
          switch (session?.user.role) {
            case Roles.CompanyManager:
              return openModal((props) => ManagerEditUsersPopup({ ...props, id: row.userId }))
            case Roles.HyperAdmin:
            case Roles.SuperAdmin:
              return openModal((props) => SuperAdminEditUsersPopup({ ...props, id: row.userId }))
          }
        },
      },
      {
        icon: 'svgfont-remove',
        title: 'elements.table.cards.menu.edit.remove',
        action: () => openModal((props) => ManagerRemoveCardPopup({ ...props, card: row })),
      },
    ]
  }

  menu = extendCommunalCardsMenuPopup({
    menu,
    navigate,
    row,
    services,
    session
  })

  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

export const ManagerCardsPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()

  // useResponsiveChange()

  const [searchParams] = useSearchParams()
  const searchFromUrl = searchParams.get("search") ?? ""

  const initialPageQuery = useMemo(
    () => ({
      pageNumber: 1,
      pageSize: 30,
      search: searchFromUrl,
    }),
    [searchFromUrl]
  )

  const tableParams: SearchTableParams<SimpleCard> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleCard>> => {
      return services.session.fetchWithToken(`/api/communal/cards/search?${pageQueryParams(q)}`).then(r => {
        if (r.status === 200) return r.json()
        return Promise.reject()
      })
    },
    pageQuery: initialPageQuery,
    toolbox: () => <>
      <div className='tool' onClick={() => openModal((props) => ManagerInsertCardPopup({ ...props, services, session }))}><i className='svgfont-add' /></div>
    </>,
    defaultSortBy: 'ucid',
    header: (query, onSort) => <>
      <SortableHeader field='ucid' query={query} onSort={onSort}>{session.translates.for('elements.table.cards.ucid')}</SortableHeader>
      <SortableHeader field='username' query={query} onSort={onSort}>{session.translates.for('elements.table.cards.username')}</SortableHeader>
      <div className='hcol'></div>
    </>,
    row: (row) => <>
      <div className='col'>{row.ucid}</div>
      <div className='col'>{row.username}</div>
      <div className='col icocol' onClick={() => openModal((props) => CardsMenuPopup({ ...props, row: row }))}>
        <i className='svgfont-menu' />
      </div>
    </>,
    translates: session.translates,
    searchByNFC: true
  }

  return <LoggedInPageLayout pageName='communal-cards' services={services} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>
    <SearchTable ref={searchTableRef} {...tableParams} />
  </LoggedInPageLayout>
}